import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useI18n } from "../i18n/LanguageContext";
import { legalPagesByLang } from "../i18n/legal";
import { ArrowRight } from "lucide-react";

const SLUG_MAP = {
  "mentions-legales": "mentions",
  confidentialite: "privacy",
  cgv: "terms",
  impressum: "mentions",
  datenschutz: "privacy",
  agb: "terms",
  "legal-notice": "mentions",
  privacy: "privacy",
  terms: "terms",
};

const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-white">
        {p.slice(2, -2)}
      </strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
};

export default function Legal() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const pages = legalPagesByLang(lang);

  const key = SLUG_MAP[slug];
  if (!key) return <Navigate to="/" replace />;

  const page = pages[key];

  const allLinks = [
    { key: "mentions", page: pages.mentions },
    { key: "privacy", page: pages.privacy },
    { key: "terms", page: pages.terms },
  ];

  return (
    <div data-testid={`page-legal-${key}`} className="bg-ct3f-bg text-white">
      <section className="border-b border-white/10 bg-flame-radial">
        <div className="px-6 md:px-12 lg:px-20 py-20 lg:py-28 relative">
          <div className="absolute inset-0 grid-lines pointer-events-none opacity-50" />
          <div className="relative">
            <span className="label-tech text-ct3f-amber">{page.eyebrow}</span>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl lg:text-8xl mt-4 leading-[0.92] max-w-5xl">
              <span className="text-white">{page.title}</span>
              <span className="text-flame">.</span>
            </h1>
            <p className="mt-6 label-tech text-white/45">{page.updated}</p>
          </div>
        </div>
      </section>

      <section>
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <aside className="lg:col-span-3 border-b lg:border-b-0 lg:border-r border-white/10 px-6 md:px-12 lg:px-10 py-12 bg-[#0E0E10]">
            <span className="label-tech text-white/40">DOCUMENTS</span>
            <ul className="mt-6 space-y-4">
              {allLinks.map(({ key: k, page: p }) => {
                const active = k === key;
                return (
                  <li key={k}>
                    <Link
                      to={`/${p.slug}`}
                      data-testid={`legal-nav-${k}`}
                      className={`group flex items-start justify-between gap-3 border-l-2 pl-3 transition-colors ${
                        active
                          ? "border-ct3f-orange text-white"
                          : "border-transparent text-white/50 hover:text-white hover:border-white/30"
                      }`}
                    >
                      <span className="font-display font-bold text-lg leading-tight">
                        {p.label}
                      </span>
                      <ArrowRight size={14} className={`mt-1.5 shrink-0 ${active ? "text-ct3f-amber" : ""}`} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </aside>

          <article className="lg:col-span-9 px-6 md:px-12 lg:px-16 py-12 lg:py-16">
            <div className="max-w-3xl space-y-12">
              {page.sections.map((s, i) => (
                <section key={i} data-testid={`legal-section-${i}`}>
                  <div className="flex items-baseline gap-4 border-b border-white/10 pb-3 mb-5">
                    <span className="label-tech text-ct3f-amber">§ {String(i + 1).padStart(2, "0")}</span>
                    <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight text-white">
                      {s.h.replace(/^\d+\.\s*/, "")}
                    </h2>
                  </div>
                  <div className="space-y-4 text-white/75 leading-relaxed">
                    {s.p.map((para, j) => (
                      <p key={j}>{renderInline(para)}</p>
                    ))}
                  </div>
                </section>
              ))}

              <div className="pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
                <p className="label-tech text-white/40">CT3F · {page.label.toUpperCase()}</p>
                <Link
                  to="/contact"
                  data-testid="legal-cta-contact"
                  className="group inline-flex items-center gap-3 bg-flame text-white label-tech px-6 py-4 hover:brightness-110 transition-all"
                >
                  {t.nav.cta}
                  <ArrowRight size={16} className="hover-arrow" />
                </Link>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
